import dayjs from 'dayjs';
import * as React from 'react';
import { useIntl } from 'react-intl';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  ADDRESS_KEY,
  AVARAGE_CONSUMPTION_KEY,
  AVARAGE_SPEED_KEY,
  BOTH_KEY,
  CO2_EMISSION_KEY,
  CUSTOMER_KEY,
  DAILY_KEY,
  DECLINE_KEY,
  DISTANCE_KEY,
  DRIVER_KEY,
  DURATION_KEY,
  ECO_DRIVE_KEY,
  FUEL_CONSUMPTION_CAN_KEY,
  INCREASE_KEY,
  INFO_FOR_PARKING_KEY,
  INFO_KEY,
  MAXIMAL_SPEED_KEY,
  MILEAGE_END_KEY,
  MONTHLY_KEY,
  OVERVIEW_KEY,
  PRIVATE_KEY,
  PRIVATE_SECTION_KEY,
  PUBLIC_KEY,
  SOURCE_KEY,
  TOTAL_KEY,
  WEEKLY_KEY,
} from '../constants';
import { getLanguage } from '../../../Services/UserService';
import { EN_LANGUAGE, getDayjsLocale } from '../../../Services/LanguageService';
import SectionEcoDriveCell from '../SectionCell/SectionEcoDriveCell';
import {
  ICheckedRecord,
  ICheckedTableItems,
  ITableSettingsData,
  ITripsOverViewSummary,
  ITripsOverviewColumn,
} from '../interfaces';
import {
  checkedTableItemsAtom,
  columnsAtom,
  headerWidthAtom,
  hiddenSummaryItemIndexesAtom,
  isTableSettingsMenuOpenedAtom,
  rideItemAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  showSummariesAtom,
  tableSettingsStateAtom,
} from '../TripsOverviewState';
import SectionCo2EmissionCell from '../SectionCell/SectionCo2EmissionCell';
import useAnalytics from '../../../hooks/useAnalytics';
import useTable from '../../CmdTable/hooks/useTable';
import { GDPR_VALUE } from '../../CmdTable/constants';

interface ISectionSummaryProps {
  rideId: string;
  selectedCheckbox: ICheckedRecord[];
  selectSectionCheckbox: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sectionSummaryData: ITripsOverViewSummary[];
  index: number;
}

function SectionSummary(props: ISectionSummaryProps): JSX.Element {
  const rideItem = useRecoilValue(rideItemAtom(props.rideId));
  const tableSettingsState = useRecoilValue<ITableSettingsData | null>(tableSettingsStateAtom);
  const [hiddenSummaryItemIndexes, setHiddenSummaryItemIndexes] = useRecoilState<number[]>(
    hiddenSummaryItemIndexesAtom
  );
  const [checkedTableItems, setCheckedTableItems] =
    useRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const rideItemIds = useRecoilValue<string[]>(rideItemIdsAtom);
  const rideItemTimestamps = useRecoilValue<string[]>(rideItemTimestampsAtom);
  const { formatMessage: f } = useIntl();
  const [isSectionChecked, setIsSectionChecked] = React.useState(false);
  dayjs.extend(weekOfYear);
  const showSummaries = useRecoilValue(showSummariesAtom);
  const columns = useRecoilValue<ITripsOverviewColumn[]>(columnsAtom);

  const [resizedTable, setResizedTable] = React.useState(false);
  const isTableSettingsMenuOpened = useRecoilValue(isTableSettingsMenuOpenedAtom);
  const detailWidth = useRecoilValue<string>(headerWidthAtom);
  const { handleAnonymizedData } = useTable();
  const { trackEvent } = useAnalytics();

  let { current: draggedRef } = React.useRef(false);

  React.useEffect(() => {
    console.info('Table resized');
    setResizedTable(!resizedTable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTableSettingsMenuOpened]);

  const getSectionInterval = React.useCallback(
    (sectionSummaryData: ITripsOverViewSummary[]) => {
      return rideItem
        ? sectionSummaryData.find(
            (item) =>
              item.type !== OVERVIEW_KEY &&
              new Date(rideItem.timestamp.start) >= new Date(item.from) &&
              new Date(rideItem.timestamp.start) <= new Date(item.to)
          )
        : null;
    },
    [rideItem]
  );

  const getFormattedDate = (typeOfDate?: string) => {
    if (rideItem) {
      const defaultLanguage = getLanguage();
      const minDate = getSectionInterval(props.sectionSummaryData)?.from;
      const dateTimeFrom = dayjs(minDate);
      const datetimeTo = getSectionInterval(props.sectionSummaryData)?.to;
      const filterType = props.sectionSummaryData
        .map((item) => item.type)
        .find((item) => item !== OVERVIEW_KEY);

      switch (filterType) {
        case DAILY_KEY:
          return `${f({
            id: `date.${dateTimeFrom.format('dddd').toLowerCase()}`,
          })} ${dateTimeFrom.format('D.M.YYYY')} ${
            typeOfDate
              ? `- ${f({
                  id: `table_list.partial.summary.rideBook.${typeOfDate}`,
                })}`
              : ''
          }`;

        case WEEKLY_KEY:
          return `${
            defaultLanguage === EN_LANGUAGE
              ? ` ${f({
                  id: 'tables.tripsOverview.table.row.summaryLines.weeks',
                })} ${dayjs(dateTimeFrom).locale(getDayjsLocale(defaultLanguage)).week()}:`
              : `${dayjs(dateTimeFrom).locale(getDayjsLocale(defaultLanguage)).week()}. ${f({
                  id: 'tables.tripsOverview.table.row.summaryLines.weeks',
                })}:`
          } ${dateTimeFrom.format('D.M.YYYY')}
          ${datetimeTo ? `- ${dayjs(datetimeTo).format('D.M.YYYY')}` : ''}
          ${
            typeOfDate
              ? `- ${f({
                  id: `table_list.partial.summary.rideBook.${typeOfDate}`,
                })}`
              : ''
          }`;
        case MONTHLY_KEY:
          return `${f({
            id: `date.${dateTimeFrom.format('MMMM').toLowerCase()}`,
          })} ${dateTimeFrom.format('YYYY')} ${
            typeOfDate
              ? `- ${f({
                  id: `table_list.partial.summary.rideBook.${typeOfDate}`,
                })}`
              : ''
          }`;
        case TOTAL_KEY:
          return `${f({
            id: 'table_list.partial.summary.rideBook.totalRow',
          })}: ${dateTimeFrom.format('D.M.YYYY')} - ${dayjs(datetimeTo).format('D.M.YYYY')} ${
            typeOfDate
              ? `- ${f({
                  id: `table_list.partial.summary.rideBook.${typeOfDate}`,
                })}`
              : ''
          }`;
        default:
          return '';
      }
    }
    return '';
  };

  const handleSectionCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSectionChecked(!isSectionChecked);

    props.selectSectionCheckbox(event);
  };

  const handleStopPropagationCheckbox = (event: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
    event.stopPropagation();
  };

  const getIsWholeSectionInRideList = React.useCallback(
    (intervalMin: string, intervalMax: string) => {
      if (checkedTableItems.counts) {
        let count = 0;
        Object.keys(checkedTableItems.rideIds).forEach((customerId: string) => {
          checkedTableItems.rideIds[customerId].forEach((rideId: string) => {
            const rideIndex = rideItemIds.indexOf(rideId);
            const rideTimestamp = rideItemTimestamps[rideIndex];
            if (
              new Date(rideTimestamp) >= new Date(intervalMin) &&
              new Date(rideTimestamp) <= new Date(intervalMax)
            ) {
              count += 1;
            }
          });
        });
        return String(count) === checkedTableItems.counts[intervalMin];
      }
      return false;
    },
    [checkedTableItems.counts, checkedTableItems.rideIds, rideItemIds, rideItemTimestamps]
  );

  const getIsAllSectioInRideBlackList = React.useCallback(
    (intervalMin: string, intervalMax: string) => {
      if (checkedTableItems.counts) {
        let count = 0;
        Object.keys(checkedTableItems.rideIdBlacklist).forEach((customerId: string) => {
          checkedTableItems.rideIdBlacklist[customerId].forEach((rideId: string) => {
            const rideIndex = rideItemIds.indexOf(rideId);
            const rideTimestamp = rideItemTimestamps[rideIndex];
            if (
              new Date(rideTimestamp) >= new Date(intervalMin) &&
              new Date(rideTimestamp) <= new Date(intervalMax)
            ) {
              count += 1;
            }
          });
        });
        const result = String(count) === checkedTableItems.counts[intervalMin];
        if (result) {
          const tempItems = { ...checkedTableItems };
          const intervals = tempItems.intervals.filter(
            (i) => i.min !== intervalMin && i.max !== intervalMax
          );
          tempItems.intervals = intervals;
          setCheckedTableItems(tempItems);
        }
        return result;
      }
      return false;
    },
    [checkedTableItems, rideItemIds, rideItemTimestamps, setCheckedTableItems]
  );

  const getIsSectioInRideList = React.useCallback(
    (intervalMin: string, intervalMax: string) => {
      let isInList = false;
      Object.keys(checkedTableItems.rideIds).forEach((customerId: string) => {
        checkedTableItems.rideIds[customerId].forEach((rideId: string) => {
          const rideIndex = rideItemIds.indexOf(rideId);
          const rideTimestamp = rideItemTimestamps[rideIndex];
          if (
            new Date(rideTimestamp) >= new Date(intervalMin) &&
            new Date(rideTimestamp) <= new Date(intervalMax)
          ) {
            isInList = true;
          }
        });
      });
      return isInList;
    },
    [checkedTableItems.rideIds, rideItemIds, rideItemTimestamps]
  );

  const getIsSectioInRideBlacklist = React.useCallback(
    (intervalMin: string, intervalMax: string) => {
      let isBlocked = false;
      Object.keys(checkedTableItems.rideIdBlacklist).forEach((customerId: string) => {
        checkedTableItems.rideIdBlacklist[customerId].forEach((rideId: string) => {
          const rideIndex = rideItemIds.indexOf(rideId);
          const rideTimestamp = rideItemTimestamps[rideIndex];
          if (
            new Date(rideTimestamp) >= new Date(intervalMin) &&
            new Date(rideTimestamp) <= new Date(intervalMax)
          ) {
            isBlocked = true;
          }
        });
      });
      return isBlocked;
    },
    [checkedTableItems.rideIdBlacklist, rideItemIds, rideItemTimestamps]
  );

  const getIsSectionIntervalInBlacklist = React.useCallback(
    (min: string, max: string, intervalBlacklist: { min: string; max: string }[]) => {
      return intervalBlacklist.find((i) => i.min === min && i.max === max);
    },
    []
  );

  React.useEffect(() => {
    if (rideItem) {
      const checkbox = document.getElementById(rideItem.timestamp.start) as HTMLInputElement;
      const interval = getSectionInterval(props.sectionSummaryData);
      const minDate = interval?.from || '';
      const maxDate = interval?.to;

      if (maxDate && checkbox) {
        const sectionIsCheckedBecauseOfMainCheckbox =
          checkedTableItems.mainCheckbox &&
          !getIsSectioInRideBlacklist(minDate, maxDate) &&
          !getIsSectionIntervalInBlacklist(minDate, maxDate, checkedTableItems.intervalsBlacklist);
        const isSectionCheckboxChecked =
          checkedTableItems.intervals.find(
            (i) => String(i.min) === String(minDate) && String(i.max) === String(maxDate)
          ) || sectionIsCheckedBecauseOfMainCheckbox;
        if (
          (isSectionCheckboxChecked &&
            !getIsSectioInRideBlacklist(minDate, maxDate) &&
            !getIsSectionIntervalInBlacklist(
              minDate,
              maxDate,
              checkedTableItems.intervalsBlacklist
            )) ||
          getIsWholeSectionInRideList(minDate, maxDate)
        ) {
          checkbox.checked = true;
          checkbox.indeterminate = false;
        } else if (
          (checkedTableItems.mainCheckbox && getIsSectioInRideBlacklist(minDate, maxDate)) ||
          (isSectionCheckboxChecked && !getIsAllSectioInRideBlackList(minDate, maxDate)) ||
          getIsSectioInRideList(minDate, maxDate)
        ) {
          checkbox.indeterminate = true;
          checkbox.checked = false;
        } else {
          checkbox.indeterminate = false;
          checkbox.checked = false;
        }
      }
    }
  }, [
    checkedTableItems.intervals,
    checkedTableItems.intervalsBlacklist,
    checkedTableItems.mainCheckbox,
    getIsAllSectioInRideBlackList,
    getIsSectioInRideBlacklist,
    getIsSectioInRideList,
    getIsSectionIntervalInBlacklist,
    getIsWholeSectionInRideList,
    getSectionInterval,
    props.sectionSummaryData,
    rideItem,
  ]);

  const renderCheckbox = (): JSX.Element | null => {
    if (rideItem) {
      let findDate = props.selectedCheckbox;

      let findChecked = props.selectedCheckbox;

      if (props.selectedCheckbox) {
        findDate = props.selectedCheckbox.filter(
          (item: ICheckedRecord) => item.startTimestamp === rideItem.timestamp.start
        );

        findChecked = props.selectedCheckbox.filter(
          (item: ICheckedRecord) => item.startTimestamp === rideItem.timestamp.start && item.checked
        );
      }

      if (
        tableSettingsState?.periodicSummaries.displayData &&
        tableSettingsState?.periodicSummaries.displayData?.length
      ) {
        return (
          <div className="b-table-prehlad-jazd-row-summary-checkbox">
            <div className="e-checkbox checkbox-sm">
              <label
                htmlFor={rideItem.timestamp.start}
                style={{ position: 'relative' }}
                onClick={handleStopPropagationCheckbox}
              >
                <input
                  id={rideItem.timestamp.start}
                  name={rideItem.timestamp.start}
                  data-min-timestamp={getSectionInterval(props.sectionSummaryData)?.from}
                  data-max-timestamp={getSectionInterval(props.sectionSummaryData)?.to}
                  data-count={getSectionInterval(props.sectionSummaryData)?.rideCount}
                  data-customer-id={rideItem.customer.id}
                  type="checkbox"
                  // checked={!!(findDate && findChecked && findDate.length === findChecked.length)}
                  onChange={handleSectionCheckbox}
                  data-cy="ride-book-section-checkbox"
                />
                <span className="p-0" />
              </label>
            </div>
          </div>
        );
      }
      return null;
    }
    return null;
  };

  const showSection = (event: React.MouseEvent<HTMLAnchorElement | HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['ride book', 'click', 'Ride book show section summary']);
    // const id = event.currentTarget.getAttribute('data-id');
    if (draggedRef) {
      return;
    }

    // if (id) props.handleIsSectionOpen(id, !props.isSectionOpen[id]);
    if (hiddenSummaryItemIndexes.includes(props.index)) {
      const newHiddenIndexes = hiddenSummaryItemIndexes.filter((i) => i !== props.index);
      setHiddenSummaryItemIndexes(newHiddenIndexes);
    } else {
      const hiddenIndexes = [...hiddenSummaryItemIndexes];
      hiddenIndexes.push(props.index);
      setHiddenSummaryItemIndexes(hiddenIndexes);
    }
  };

  const getAverageConsumptionValues = (value: string) => {
    const valueArray = value.split(' / ');
    if (valueArray.length === 2) {
      return `${valueArray[0]}<br/>${valueArray[1]}`;
    }
    return value;
  };

  const renderCell = (
    column: ITripsOverviewColumn,
    item: ITripsOverViewSummary[],
    type: string
  ): JSX.Element | null => {
    if (rideItem) {
      const date = new Date(rideItem.timestamp.start);

      const findDate = item.find(
        (findItem) => date >= new Date(findItem.from) && date <= new Date(findItem.to)
      );

      if (findDate) {
        const findKey = findDate.cells.find(
          (findCell) => findCell.name === column.key || `${findCell.name}Can` === column.key
        );

        if (findKey) {
          // cell with data
          switch (column.key) {
            case ECO_DRIVE_KEY:
              if (findKey.value[type]) {
                return <SectionEcoDriveCell value={String(findKey.value[type])} />;
              }
              return (
                <div
                  className="align-middle text-right justify-content-center"
                  style={{ minWidth: '88px', maxWidth: '88px', flex: 1 }}
                >
                  <i className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray" />
                </div>
              );

            case CO2_EMISSION_KEY:
              if (findKey.value[type]) {
                return <SectionCo2EmissionCell value={String(findKey.value[type])} />;
              }
              return (
                <div
                  className="align-middle text-right justify-content-center"
                  style={{ minWidth: '88px', maxWidth: '88px', flex: 1 }}
                >
                  <i className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray" />
                </div>
              );

            case DURATION_KEY:
              return (
                <div
                  className={`align-middle text-right justify-content-${
                    findKey.value[type] === GDPR_VALUE ? 'center' : 'end'
                  }`}
                  style={{ whiteSpace: 'nowrap', minWidth: '180px', flex: 1 }}
                >
                  {handleAnonymizedData(findKey.value[type])}
                </div>
              );

            case INFO_FOR_PARKING_KEY:
              return (
                <div
                  className={`align-middle text-right justify-content-${
                    findKey.value[type] === GDPR_VALUE ? 'center' : 'end'
                  }`}
                  style={{ minWidth: '130px', flex: 1 }}
                >
                  {handleAnonymizedData(findKey.value[type])}
                </div>
              );

            case MAXIMAL_SPEED_KEY:
              return (
                <div
                  className={`align-middle text-right justify-content-${
                    findKey.value[type] === GDPR_VALUE ? 'center' : 'end'
                  }`}
                  style={{ minWidth: '100px', flex: 1 }}
                >
                  {handleAnonymizedData(findKey.value[type])}
                </div>
              );

            case AVARAGE_SPEED_KEY:
              return (
                <div
                  className={`align-middle text-right justify-content-${
                    findKey.value[type] === GDPR_VALUE ? 'center' : 'end'
                  }`}
                  style={{ minWidth: '100px', flex: 1 }}
                >
                  {handleAnonymizedData(findKey.value[type])}
                </div>
              );

            case DECLINE_KEY:
              return (
                <div
                  className={`align-middle text-right justify-content-${
                    findKey.value[type] === GDPR_VALUE ? 'center' : 'end'
                  }`}
                  style={{ minWidth: '100px', flex: 1 }}
                >
                  {handleAnonymizedData(findKey.value[type])}
                </div>
              );

            case FUEL_CONSUMPTION_CAN_KEY:
              return (
                <div
                  className={`align-middle text-right justify-content-${
                    findKey.value[type] === GDPR_VALUE ? 'center' : 'end'
                  }`}
                  style={{ minWidth: '200px', flex: 1 }}
                >
                  {findKey.value && findKey.value[type] ? findKey.value[type] : '-'}
                </div>
              );

            case AVARAGE_CONSUMPTION_KEY:
              return (
                <div
                  className={`align-middle text-right justify-content-${
                    findKey.value[type] === GDPR_VALUE ? 'center' : 'end'
                  }`}
                  style={{ minWidth: '200px', flex: 1 }}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html:
                      findKey.value && findKey.value[type]
                        ? getAverageConsumptionValues(findKey.value[type])
                        : '-',
                  }}
                />
              );

            case DISTANCE_KEY:
              return (
                <div
                  className={`align-middle text-right justify-content-${
                    findKey.value[type] === GDPR_VALUE ? 'center' : 'end'
                  }`}
                  style={{ minWidth: '130px', flex: 1 }}
                >
                  {handleAnonymizedData(findKey.value[type])}
                </div>
              );

            case INCREASE_KEY:
              return (
                <div
                  className={`align-middle text-right justify-content-${
                    findKey.value[type] === GDPR_VALUE ? 'center' : 'end'
                  }`}
                  style={{ minWidth: '100px', flex: 1 }}
                >
                  {handleAnonymizedData(findKey.value[type])}
                </div>
              );

            default:
              return null;
          }
        }
      }

      // empty cell
      switch (column.key) {
        case PRIVATE_KEY:
          return (
            <div
              className="align-middle text-right justify-content-end"
              style={{ minWidth: '64px', maxWidth: '64px', flex: 1 }}
            />
          );
        case ADDRESS_KEY:
          return (
            <div
              className="align-middle text-right justify-content-end"
              style={{ minWidth: '350px', flex: 1 }}
            />
          );
        case INFO_KEY:
          return (
            <div
              className="align-middle text-right justify-content-end"
              style={{ minWidth: '200px', flex: 1 }}
            />
          );

        case DRIVER_KEY:
          return (
            <div
              className="align-middle text-right justify-content-end"
              style={{ minWidth: '120px', flex: 1 }}
            />
          );

        case CUSTOMER_KEY:
          return (
            <div
              className="align-middle text-right justify-content-end"
              style={{ minWidth: '100px', flex: 1 }}
            />
          );

        case MILEAGE_END_KEY:
          return (
            <div
              className="align-middle text-right justify-content-end"
              style={{ minWidth: '100px', flex: 1 }}
            />
          );

        case SOURCE_KEY:
          return (
            <div
              className="align-middle text-right justify-content-end"
              style={{ minWidth: '100px', flex: 1 }}
            />
          );

        default:
          return null;
      }
    }
    return null;
  };

  const sectionFilter = props.sectionSummaryData.filter((item) => item.type !== OVERVIEW_KEY);

  const dateTimeColElement = document.getElementById('table-header-col-dateTime');
  const vehicleColElement = document.getElementById('table-header-col-vehicle');
  let summaryColWidth = '300px';
  if (dateTimeColElement && vehicleColElement) {
    summaryColWidth = `${dateTimeColElement.offsetWidth + vehicleColElement.offsetWidth + 40}px`;
  }

  const handleMouseDown = () => {
    draggedRef = false;
  };

  const handleMouseMove = () => {
    draggedRef = true;
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {rideItem && detailWidth && (
        <div className="b-table-prehlad-jazd-row-summary">
          {renderCheckbox()}
          <div>
            <div
              className="b-table-prehlad-jazd-row-summary-item"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onClick={showSection}
              data-id={rideItem.timestamp.start}
              data-cy="ride-book-section-toggleShowDetail-click"
              style={{ minWidth: `${detailWidth}`, flex: 1, userSelect: 'none' }}
            >
              {tableSettingsState?.periodicSummaries.displayData &&
                tableSettingsState?.periodicSummaries.displayData.includes(BOTH_KEY) &&
                showSummaries && (
                  <div
                    className="align-middle pl-5"
                    style={{ minWidth: `${summaryColWidth}`, flex: 1 }}
                  >
                    <strong>{getFormattedDate()}</strong>
                  </div>
                )}

              {/* Main section with total */}
              {tableSettingsState?.periodicSummaries.displayData &&
                tableSettingsState?.periodicSummaries.displayData.includes(BOTH_KEY) &&
                showSummaries &&
                sectionFilter &&
                columns &&
                columns.map((column) => (
                  <React.Fragment key={column.key}>
                    {!column.hidden && renderCell(column, sectionFilter, TOTAL_KEY)}
                  </React.Fragment>
                ))}
            </div>

            {/* Sekcia sluzobne jazdy */}
            {tableSettingsState?.periodicSummaries.displayData &&
              tableSettingsState?.periodicSummaries.displayData.includes(PUBLIC_KEY) &&
              showSummaries && (
                <div
                  key={`${rideItem.timestamp.start}-public`}
                  className="b-table-prehlad-jazd-row-summary-item"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onClick={showSection}
                  data-id={rideItem.timestamp.start}
                  data-cy="ride-book-section-toggleShowDetail-click"
                  style={{ userSelect: 'none' }}
                >
                  <div
                    className="align-middle pl-5"
                    style={{ minWidth: `${summaryColWidth}`, flex: 1 }}
                  >
                    <strong>{getFormattedDate(PUBLIC_KEY)}</strong>
                  </div>
                  {sectionFilter &&
                    columns &&
                    columns.map((column) => (
                      <React.Fragment key={column.key}>
                        {!column.hidden && renderCell(column, sectionFilter, PUBLIC_KEY)}
                      </React.Fragment>
                    ))}
                </div>
              )}

            {/* Sekcia sukromene jazdy */}
            {tableSettingsState?.periodicSummaries.displayData &&
              tableSettingsState?.periodicSummaries.displayData.includes(PRIVATE_SECTION_KEY) &&
              showSummaries && (
                <div
                  key={`${rideItem.timestamp.start}-private`}
                  className="b-table-prehlad-jazd-row-summary-item"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onClick={showSection}
                  data-id={rideItem.timestamp.start}
                  data-cy="ride-book-section-toggleShowDetail-click"
                  style={{ userSelect: 'none' }}
                >
                  <div
                    className="align-middle pl-5"
                    style={{ minWidth: `${summaryColWidth}`, flex: 1 }}
                  >
                    <strong>{getFormattedDate(PRIVATE_SECTION_KEY)}</strong>
                  </div>
                  {sectionFilter &&
                    columns &&
                    columns.map((column) => (
                      <React.Fragment key={column.key}>
                        {!column.hidden && renderCell(column, sectionFilter, PRIVATE_SECTION_KEY)}
                      </React.Fragment>
                    ))}
                </div>
              )}
          </div>
          {showSummaries && (
            <div className="text-center align-middle sticky-col">
              <div className="dropdown actions">
                <a
                  href="#"
                  title={
                    hiddenSummaryItemIndexes.includes(props.index)
                      ? f({ id: 'bulk.show.detail.row' })
                      : f({ id: 'bulk.hide.detail.row' })
                  }
                  className="text-gray-lighten"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onClick={showSection}
                  data-id={rideItem.timestamp.start}
                  data-cy="ride-book-section-toggleShowDetail"
                >
                  <i
                    className={`cmd-icons cmd-icon-${
                      hiddenSummaryItemIndexes.includes(props.index) ? 'dropup' : 'dropdown'
                    } fs-7`}
                  />
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default SectionSummary;
